<template>
  <v-dialog v-model="dialog" max-width="750px" persistent>
    <v-card>
      <v-card-title class="headline" primary-title>
        Register Root Certificate Authority
      </v-card-title>

      <!-- STEP 1: Get registration code -->
      <v-card-text v-if="step === 1">
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex xs12>
              <v-text-field
                v-model="form.ca_name"
                label="CA Name"
                placeholder="e.g., MyCompany Root CA"
                outlined
                required
              ></v-text-field>
            </v-flex>

            <v-flex xs12>
              <v-textarea
                v-model="form.ca_certificate"
                label="CA Certificate (PEM)"
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                outlined
                required
                rows="7"
              ></v-textarea>
            </v-flex>

            <v-flex xs12>
              <v-text-field
                v-model="form.notes"
                label="Notes (Optional)"
                outlined
              ></v-text-field>
            </v-flex>

            <v-flex xs12>
              <v-alert type="info" outlined>
                <div class="body-2">
                  Step 1 of 2 — AWS requires you to prove ownership of the CA private key.
                  Click <strong>Get Registration Code</strong> to receive a one-time code from AWS,
                  then sign it with your CA private key to produce a verification certificate.
                </div>
              </v-alert>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>

      <!-- STEP 2: Sign & submit verification cert -->
      <v-card-text v-if="step === 2">
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex xs12>
              <v-alert type="success" outlined>
                <div class="body-2">
                  <strong>Registration code obtained.</strong>
                  Run the commands below using your CA private key, then paste the resulting
                  <code>verify-cert.pem</code> in the field below.
                </div>
              </v-alert>
            </v-flex>

            <v-flex xs12>
              <v-text-field
                :value="registrationCode"
                label="AWS Registration Code"
                outlined
                readonly
                append-icon="content_copy"
                v-on:click:append="copyCode"
              ></v-text-field>
            </v-flex>

            <v-flex xs12>
              <v-card outlined class="pa-3" style="background:#f5f5f5;">
                <div class="body-2" style="font-family:monospace; white-space:pre-wrap; font-size:0.78rem;">openssl ecparam -name prime256v1 -genkey -noout -out verify-key.pem
openssl req -new -key verify-key.pem -out verify.csr \
  -subj "/CN={{ registrationCode }}"
openssl x509 -req -in verify.csr \
  -CA ca-cert.pem -CAkey ca-key.pem -CAcreateserial \
  -out verify-cert.pem -days 1 -sha256</div>
              </v-card>
            </v-flex>

            <v-flex xs12>
              <v-textarea
                v-model="form.ca_verification_certificate"
                label="Verification Certificate (PEM)"
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                outlined
                required
                rows="7"
              ></v-textarea>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-btn color="grey darken-1" text v-on:click="abort">Cancel</v-btn>
        <v-spacer></v-spacer>

        <!-- Step 1 action -->
        <v-btn
          v-if="step === 1"
          color="indigo"
          text
          v-on:click="getRegistrationCode"
          :loading="isFetching"
          :disabled="!isStep1Valid"
        >
          Get Registration Code
        </v-btn>

        <!-- Step 2 actions -->
        <v-btn v-if="step === 2" text v-on:click="step = 1">Back</v-btn>
        <v-btn
          v-if="step === 2"
          color="indigo"
          text
          v-on:click="createCA"
          :loading="isCreating"
          :disabled="!isStep2Valid"
        >
          Register CA
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  data() {
    return {
      dialog: true,
      step: 1,
      isFetching: false,
      isCreating: false,
      registrationCode: "",
      form: {
        ca_name: "",
        ca_certificate: "",
        ca_verification_certificate: "",
        notes: "",
      },
    };
  },

  computed: {
    isStep1Valid() {
      return (
        this.form.ca_name.trim().length > 0 &&
        this.form.ca_certificate.trim().startsWith("-----BEGIN")
      );
    },
    isStep2Valid() {
      return (
        this.form.ca_verification_certificate.trim().startsWith("-----BEGIN")
      );
    },
  },

  methods: {
    async getRegistrationCode() {
      this.isFetching = true;
      try {
        this.registrationCode = await this.$store.dispatch(
          "rootCAs/getRegistrationCode",
        );
        this.step = 2;
      } catch (error) {
        console.error("Error getting registration code:", error);
      } finally {
        this.isFetching = false;
      }
    },

    async createCA() {
      if (!this.isStep2Valid) return;
      this.isCreating = true;
      try {
        const payload = {
          ca_name: this.form.ca_name,
          ca_certificate: this.form.ca_certificate.trim(),
          ca_verification_certificate: this.form.ca_verification_certificate.trim(),
          notes: this.form.notes || null,
        };
        await this.$store.dispatch("rootCAs/createRootCA", payload);
        this.$emit("completed");
      } catch (error) {
        console.error("Error registering Root CA:", error);
      } finally {
        this.isCreating = false;
      }
    },

    copyCode() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.registrationCode);
      }
    },

    abort() {
      this.$emit("abort");
    },
  },
};
</script>
